import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { axiosInstance as axios } from '../context/AuthContext';
import { 
  TrendingUp, TrendingDown, Minus, Users, Calendar, 
  BookOpen, Heart, Camera, MessageSquare, Award,
  ChevronLeft, RefreshCw, BarChart3, Lightbulb
} from 'lucide-react';
import { useSubscription, FEATURES } from '../hooks/useSubscription';
import UpgradeModal from '../components/UpgradeModal';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export default function FamilyAnalytics() {
  const { familyId } = useParams();
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchAnalytics(),
        fetchInsights(),
        fetchLeaderboard(),
        fetchActivityFeed()
      ]);
      setLoading(false);
    };
    
    fetchAll();
  }, [familyId, period]);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/families/${familyId}/analytics?days=${period === 'week' ? 7 : period === 'month' ? 30 : 365}`);
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const fetchInsights = async () => {
    try {
      const res = await axios.get(`${API}/families/${familyId}/analytics/insights`);
      setInsights(res.data);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/families/${familyId}/analytics/leaderboard?period=${period}`);
      setLeaderboard(res.data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  const fetchActivityFeed = async () => {
    try {
      const res = await axios.get(`${API}/families/${familyId}/analytics/activity-feed?limit=20`);
      setActivityFeed(res.data.activities || []);
    } catch (err) {
      console.error('Failed to fetch activity feed:', err);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return <TrendingUp className="w-5 h-5 text-green-500" />;
    if (trend === 'decreasing') return <TrendingDown className="w-5 h-5 text-red-500" />;
    return <Minus className="w-5 h-5 text-gray-500" />;
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'positive': return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'celebration': return <Award className="w-5 h-5 text-yellow-500" />;
      case 'suggestion': return <Lightbulb className="w-5 h-5 text-blue-500" />;
      case 'reminder': return <Heart className="w-5 h-5 text-pink-500" />;
      default: return <Lightbulb className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  // Feature gate for Analytics
  if (!hasFeature(FEATURES.FAMILY_ANALYTICS)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <UpgradeModal 
          isOpen={showUpgradeModal} 
          onClose={() => setShowUpgradeModal(false)} 
          feature="family_analytics"
          requiredTier="flourishing"
        />
        
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4 mb-8">
            <Link to={`/family/${familyId}`} className="text-gray-400 hover:text-white">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-400" />
              Family Analytics
            </h1>
          </div>
          
          <div className="max-w-xl mx-auto">
            <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 rounded-xl border border-amber-500/30 p-8 text-center">
              <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-10 h-10 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Family Analytics</h2>
              <p className="text-gray-300 mb-6 max-w-md mx-auto">
                Track engagement, view activity insights, see leaderboards, 
                and understand how your family is using the platform.
              </p>
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="px-8 py-4 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors text-lg"
              >
                🌳 Upgrade to Flourishing
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to={`/family/${familyId}`} className="text-gray-400 hover:text-white">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="w-7 h-7 text-blue-400" />
                Family Analytics
              </h1>
              <p className="text-gray-400">{analytics?.family_name}</p>
            </div>
          </div>
          
          {/* Period Selector */}
          <div className="flex gap-2 bg-slate-800 rounded-lg p-1">
            {['week', 'month', 'year'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  period === p 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-slate-700">
          {['overview', 'leaderboard', 'activity'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-2 font-medium transition-colors ${
                activeTab === tab
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
              <StatCard icon={<Users />} label="Members" value={analytics?.total_members || 0} color="blue" />
              <StatCard icon={<Calendar />} label="Events" value={analytics?.total_events || 0} color="green" />
              <StatCard icon={<BookOpen />} label="Stories" value={analytics?.total_stories || 0} color="purple" />
              <StatCard icon={<Camera />} label="Photos" value={analytics?.total_photos || 0} color="pink" />
              <StatCard icon={<Heart />} label="Prayers" value={analytics?.total_prayers || 0} color="red" />
              <StatCard icon={<MessageSquare />} label="Active" value={analytics?.active_members_period || 0} color="yellow" />
            </div>

            {/* Engagement Trend */}
            {analytics?.engagement_trend && (
              <div className="bg-slate-800 rounded-xl p-6 mb-8">
                <div className="flex items-center gap-3">
                  {getTrendIcon(analytics.engagement_trend)}
                  <span className="text-white font-medium">
                    Engagement is {analytics.engagement_trend} this {period}
                  </span>
                </div>
              </div>
            )}

            {/* Insights */}
            {insights?.insights && insights.insights.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">Insights & Suggestions</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {insights.insights.map((insight, i) => (
                    <div key={i} className="bg-slate-800 rounded-xl p-5">
                      <div className="flex items-start gap-3">
                        {getInsightIcon(insight.type)}
                        <div>
                          <h3 className="text-white font-medium">{insight.title}</h3>
                          <p className="text-gray-400 text-sm mt-1">{insight.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Contributors */}
            {analytics?.top_contributors && analytics.top_contributors.length > 0 && (
              <div className="bg-slate-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Top Contributors</h2>
                <div className="space-y-3">
                  {analytics.top_contributors.slice(0, 5).map((member, i) => (
                    <div key={member.user_id} className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        i === 0 ? 'bg-yellow-500 text-yellow-900' :
                        i === 1 ? 'bg-gray-300 text-gray-700' :
                        i === 2 ? 'bg-amber-600 text-amber-100' :
                        'bg-slate-700 text-gray-300'
                      }`}>
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{member.user_name}</p>
                        <p className="text-gray-400 text-sm">{member.total_activities} activities</p>
                      </div>
                      <div className="text-right">
                        <p className="text-blue-400 font-semibold">{member.engagement_score}</p>
                        <p className="text-gray-500 text-xs">points</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'leaderboard' && leaderboard && (
          <div className="bg-slate-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Engagement Leaderboard</h2>
            {leaderboard.leaderboard?.length > 0 ? (
              <div className="space-y-4">
                {leaderboard.leaderboard.map((entry) => (
                  <div key={entry.user_id} className="flex items-center gap-4 p-4 bg-slate-700 rounded-lg">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                      entry.rank === 1 ? 'bg-yellow-500 text-yellow-900' :
                      entry.rank === 2 ? 'bg-gray-300 text-gray-700' :
                      entry.rank === 3 ? 'bg-amber-600 text-amber-100' :
                      'bg-slate-600 text-gray-300'
                    }`}>
                      {entry.rank}
                    </div>
                    {entry.picture ? (
                      <img src={entry.picture} alt={entry.user_name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                        {entry.user_name.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-white font-medium text-lg">{entry.user_name}</p>
                      <p className="text-gray-400 text-sm">{entry.activities} activities</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-400">{entry.score}</p>
                      <p className="text-gray-500 text-sm">points</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">No activity yet this {period}</p>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="bg-slate-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Recent Activity</h2>
            {activityFeed.length > 0 ? (
              <div className="space-y-4">
                {activityFeed.map((activity) => (
                  <div key={activity.activity_id} className="flex items-start gap-4 p-4 bg-slate-700 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                      {activity.user_name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1">
                      <p className="text-white">{activity.display_text}</p>
                      <p className="text-gray-500 text-sm mt-1">{formatTimeAgo(activity.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">No recent activity</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    purple: 'bg-purple-500/20 text-purple-400',
    pink: 'bg-pink-500/20 text-pink-400',
    red: 'bg-red-500/20 text-red-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
  };

  return (
    <div className="bg-slate-800 rounded-xl p-4">
      <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-3`}>
        {React.cloneElement(icon, { className: 'w-5 h-5' })}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-gray-400 text-sm">{label}</p>
    </div>
  );
}
